import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { adminAuthService, type AdminUser } from '@/services/adminAuthService';

interface AdminAuthContextValue {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_jwt');
    if (!token) {
      setIsLoading(false);
      return;
    }
    adminAuthService
      .me()
      .then((a) => {
        setAdmin(a);
      })
      .catch(() => {
        localStorage.removeItem('admin_jwt');
        setAdmin(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const handler = () => {
      setAdmin(null);
    };
    window.addEventListener('admin:unauthorized', handler);
    return () => window.removeEventListener('admin:unauthorized', handler);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await adminAuthService.login(email, password);
    localStorage.setItem('admin_jwt', response.access_token);
    setAdmin(response.admin);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_jwt');
    setAdmin(null);
  }, []);

  const value: AdminAuthContextValue = {
    admin,
    isAuthenticated: admin !== null,
    isLoading,
    login,
    logout,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  return ctx;
}
